import React, { useState, useEffect, useCallback } from 'react'

import Modal from 'react-modal'

import {
  Button,
  Close,
  Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func, number, oneOfType, string,
} from 'prop-types'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import i18n from 'Pods/Shared/I18n'
import InputField from 'Pods/Shared/InputField'
import { RuleRunner, run } from 'Pods/Shared/RuleRunner'
import { required } from 'Pods/Shared/Rules/Rules'
import TextareaField from 'Pods/Shared/TextareaField'

const fieldValidations = [
  RuleRunner('subject', 'Subject', required),
  RuleRunner('message', 'Message', required),
]

function NewContactModal({
  closeModal,
  itemId = '',
  authorUsername,
  conversationType,
  headerText = 'Message the designer',
  subtitle = null,
}) {
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  const { postData, isLoading } = useApiPost()

  useEffect(() => {
    setValidationErrors(run({ subject, message }, fieldValidations))
  }, [subject, message])

  const handleSubjectChange = useCallback((event) => {
    setSubject(event.target.value)
  }, [])

  const handleMessageChange = useCallback((event) => {
    setMessage(event.target.value)
  }, [])

  const resetForm = useCallback(() => {
    setSubject('')
    setMessage('')
    setShowErrors(false)
    setValidationErrors({})
  }, [])

  const notifySuccess = useCallback(() => {
    window.showNotification(
      i18n.t('itemPage.supportForm.messageSent', { ns: 'rails' }),
      {
        type: 'success',
        autohide: true,
      }
    )
  }, [])

  const notifyFailure = useCallback((errorMessage) => {
    window.showNotification(
      errorMessage,
      {
        type: 'error',
        autohide: true,
      }
    )
  }, [])

  const handleSubmit = useCallback((event) => {
    event.preventDefault()

    if (isLoading) {
      return
    }

    setShowErrors(true)

    const errors = run({ subject, message }, fieldValidations)
    setValidationErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    postData({
      path: '/api/internal/conversations',
      data: {
        conversation: {
          subject,
          message,
          item_id: itemId,
          gallery_id: null,
          recipient: authorUsername,
          conversation_type: conversationType,
        },
      },
      onSuccess: () => {
        notifySuccess()
        resetForm()
        closeModal()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.errors?.[0]?.detail?.message
          || 'Something went wrong, please try again'

        notifyFailure(errorMessage)
      },
    })
  }, [
    authorUsername,
    closeModal,
    conversationType,
    isLoading,
    itemId,
    message,
    notifyFailure,
    notifySuccess,
    postData,
    resetForm,
    subject,
  ])

  const handleCancel = useCallback(() => {
    resetForm()
    closeModal()
  }, [closeModal, resetForm])

  return (
    <Modal
      isOpen
      shouldCloseOnOverlayClick
      onRequestClose={handleCancel}
      contentLabel="Contact Modal"
      ariaHideApp={false}
      className={{
        base: '',
        afterOpen: 'overlay-modal--container_after-open overlay-modal-container--new-contact',
        beforeClose: 'overlay-modal--container_before-close',
      }}
      overlayClassName={{
        base: 'overlay-modal--background',
        afterOpen: 'overlay-modal--background_after-open',
        beforeClose: 'overlay-modal--background_before-close',
      }}
    >
      <div className="new-contact-modal">
        <div className="new-contact-modal__header">
          <h2 className="new-contact-modal__title">{headerText}</h2>
          <div className="new-contact-modal__close-button-container">
            <Button
              type="ghost"
              variant="only-icon"
              size="small"
              customClass="new-contact-modal__close-button"
              onClick={handleCancel}
              icon={<Icon svgComponent={Close} />}
              actionType="button"
              aria-label="Close contact modal"
            />
          </div>
        </div>
        <div className="new-contact-modal__content">
          {subtitle && <p className="new-contact-modal__subtitle">{subtitle}</p>}

          <form className="new-contact-modal__form" onSubmit={handleSubmit}>
            <InputField
              label="Subject"
              id="contact-modal-subject"
              name="subject"
              value={subject}
              formClassName="new-contact-modal__field form-group-grey"
              inputClassName="form-control-grey"
              onChange={handleSubjectChange}
              placeholder="Type here..."
              errorText={validationErrors.subject}
              showError={showErrors}
              disabled={isLoading}
            />

            <TextareaField
              id="contact-modal-message"
              name="message"
              label="Message"
              value={message}
              formClassName="new-contact-modal__field form-group-grey"
              inputClassName="form-control-grey"
              onChange={handleMessageChange}
              placeholder="Type here..."
              errorText={validationErrors.message}
              showError={showErrors}
              disabled={isLoading}
            />

            <div className="new-contact-modal__footer">
              <Button
                type="secondary"
                customClass="new-contact-modal__button"
                onClick={handleCancel}
                actionType="button"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                customClass="new-contact-modal__button new-contact-modal__button--primary"
                actionType="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send message'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  )
}

NewContactModal.propTypes = {
  closeModal: func.isRequired,
  authorUsername: string.isRequired,
  conversationType: string.isRequired,
  itemId: oneOfType([string, number]),
  headerText: string,
  subtitle: string,
}

export default NewContactModal
